import { isNullish } from '../Nullish';
import { DynamicEventEmitter } from './DynamicEventEmitter';
import { GetEventEmitterElement, IEventEmitter, IHasEventEmitter } from './types';

/**
 * EventEmitter Helper
 *
 * Add EventEmitter functions to any object.
 */
export class EventEmitterHelper implements IHasEventEmitter {
    #eventEmitter?: IEventEmitter;

    /**
     * The function used to retrieve the `HTMLElement` that will be used to
     * listen/emit events.
     */
    #getEventEmitterElement?: GetEventEmitterElement;

    /**
     * `HTMLElement` used to listen/emit events.
     */
    get #eventEmitterElement(): EventTarget {
        if (isNullish(this.#getEventEmitterElement)) {
            throw new TypeError('Emitter has not been defined.');
        }

        const element = this.#getEventEmitterElement();

        if (isNullish(element)) {
            throw new TypeError('Emitter element does not exist.');
        }

        return element;
    }

    /**
     * Define the function used to retrieve the `HTMLElement`.
     *
     * @param getEmitterElement
     */
    setEventEmitterElement(getEmitterElement: GetEventEmitterElement): void;
    /**
     * Set the `HTMLElement` that will be used to listen/emit events.
     *
     * @param emitterElement
     */
    setEventEmitterElement(emitterElement: EventTarget): void;
    setEventEmitterElement(getEmitterElement: EventTarget | GetEventEmitterElement): void {
        if (!isNullish(this.#getEventEmitterElement)) {
            throw new TypeError('Emitter element has already been set.');
        }

        if (getEmitterElement instanceof EventTarget) {
            this.#getEventEmitterElement = function () {
                return getEmitterElement;
            };
        } else {
            this.#getEventEmitterElement = getEmitterElement;
        }
    }

    get eventEmitter(): IEventEmitter {
        if (isNullish(this.#eventEmitter)) {
            const emitter = new DynamicEventEmitter();
            emitter.setEventEmitterElement(this.#eventEmitterElement);
            this.#eventEmitter = emitter;
        }
        return this.#eventEmitter;
    }
}
